import React, { useState } from "react";

import { t } from "i18next";

import { Box, TextField } from "@mui/material";

import ModalWrapper from "@/src/components/molecules/ModalWrapper";

import {
  SearchFiltersAtom,
  dealerSavedSearchFiltersAtom
} from "../../../../state/dealer/dealerActiveAuctionSearchFiltersAtom";
import { notificationAtom } from "../../../../state/notificationAtom";

import { useAtom, useSetAtom } from "jotai";

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchFilters: SearchFiltersAtom;
  onSave: (newFilterName: string) => void;
}

const SaveSearchModal = ({
  isOpen,
  onClose,
  searchFilters,
  onSave
}: SaveSearchModalProps) => {
  const [searchName, setSearchName] = useState("");

  const setNotification = useSetAtom(notificationAtom);
  const [savedSearchFilters, setSavedSearchFilters] = useAtom(
    dealerSavedSearchFiltersAtom
  );
  const [duplicateNameError, setDuplicateNameError] = useState(false);

  const handleSaveSearch = () => {
    const trimmedName = searchName.trim();

    if (!trimmedName || !searchFilters) {
      return;
    }

    setSavedSearchFilters((currentState) => {
      const updatedState = currentState ?? [];
      return [...updatedState, { name: trimmedName, searchFilters }];
    });
    setSearchName("");
    setTimeout(() => {
      setNotification({
        text: t("dealer.searchFilterMessages.searchFiltersSaved"),
        variant: "success"
      });
    }, 150);
    onSave(trimmedName);
    onClose();
  };

  const checkDuplicateName = (name: string): boolean => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return false;
    }

    const lowerCaseName = trimmedName.toLowerCase();
    return (
      savedSearchFilters?.some(
        (filter) => filter.name.trim().toLowerCase() === lowerCaseName
      ) || false
    );
  };

  return (
    <ModalWrapper
      id="saveSearchModal"
      open={isOpen}
      onClose={onClose}
      title={t("dealer.searchFilterGeneral.saveFilter") ?? ""}
      size="sm"
      secondaryAction={{
        id: "cancelSaveSearchButton",
        label: t("general.cancel"),
        onClick: onClose
      }}
      primaryAction={{
        id: "saveSearchButton",
        label: t("general.save"),
        disabled: searchName.trim() === "" || duplicateNameError,
        onClick: handleSaveSearch
      }}
    >
      <Box>
        <TextField
          id="searchTextField"
          label={t("dealer.searchFilterGeneral.filterName")}
          value={searchName}
          onChange={(e) => {
            const newName = e.target.value.toString();
            setSearchName(newName);
            setDuplicateNameError(checkDuplicateName(newName));
          }}
          error={duplicateNameError}
          helperText={
            duplicateNameError
              ? t("dealer.searchFilters.sameNameError")
              : undefined
          }
          autoFocus
          sx={{
            width: "100%",
            mb: 2,
            ".MuiFormHelperText-root": {
              position: "absolute",
              bottom: "-1.25rem",
              right: "-1rem"
            }
          }}
        />
      </Box>
    </ModalWrapper>
  );
};

export default SaveSearchModal;
